import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useHeaderGeneration } from '@/hooks/text-generation';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { researchClient } from '@/integrations/clients';
import { IntegrationError } from '@/integrations/shared';
import type { ResearchRequest } from '@/integrations/perplexity';
import type { PrivateResearchGenerationParams } from './types';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';

export const usePrivateResearchGeneration = () => {
  const queryClient = useQueryClient();
  const { generateHeader } = useHeaderGeneration();
  const { executeAIOperation } = useAIOperationWrapper();

  return useMutation({
    mutationFn: async (params: PrivateResearchGenerationParams): Promise<any> => {
      return executeAIOperation(
        async () => {
          console.log('Starting private research generation:', params);
          
          // Check if user is authenticated
          const { data: { user }, error: authError } = await supabase.auth.getUser();
          
          if (authError || !user) {
            throw new Error('You must be logged in to generate research');
          }

          // Map frontend research type names to backend-compatible format if needed
          const researchTypeMapping: Record<string, string> = {
            'Technology & Innovation': 'Technology & Innovation',
            'Business & Professional': 'Business & Professional', 
            'Content & Creative': 'Content & Creative',
            'Personal Development': 'Personal Development',
            'Academic & Learning': 'Academic & Learning',
            'Current Events & News': 'Current Events & News'
          };

          // Use the research type as-is or default to "General Research"
          const effectiveResearchType = params.researchType 
            ? (researchTypeMapping[params.researchType] || params.researchType)
            : 'General Research';

          console.log('Using research type:', effectiveResearchType);

          // Build research request for the research client
          const researchRequest: ResearchRequest = {
            researchType: effectiveResearchType,
            researchPrompt: params.researchPrompt,
            timeframe: params.timeframe,
            country: params.country,
            sourceType: params.sourceType,
            // selectedFilters removed - no longer sending dynamic filters
          };

          // Call the research client
          let researchData;
          try {
            researchData = await researchClient.generateResearch(researchRequest);
          } catch (error) {
            console.error('Private research generation error:', error);
            const errorMessage = error instanceof IntegrationError 
              ? error.message 
              : `Research generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
            throw new Error(errorMessage);
          }

          if (!researchData) {
            throw new Error('No research data returned from function');
          }

          console.log('Private research data received:', researchData);

          // Generate a header using the header generation system
          let title: string;
          try {
            console.log('Generating header for private research content...');
            const headerResult = await generateHeader({
              content: researchData.content || '',
              originalPrompt: params.researchPrompt,
              context: `Research Type: ${effectiveResearchType}${params.timeframe ? `, Timeframe: ${params.timeframe}` : ''}${params.country ? `, Country: ${params.country}` : ''}`,
            });

            title = headerResult.header;
            console.log('Generated header:', title);
          } catch (headerError) {
            console.error('Header generation failed, using fallback:', headerError);
            // Fallback to original prompt if header generation fails
            title = params.researchPrompt.substring(0, 100);
          }

          // Build research parameters object
          const researchParameters = {
            research_type: effectiveResearchType,
            timeframe: params.timeframe || null,
            timeframe_label: researchData.timeframeLabel || null,
            date_range_start: researchData.dateRangeStart || null,
            date_range_end: researchData.dateRangeEnd || null,
            country: params.country || null,
            source_type: params.sourceType || null,
            type_specific_filters: {} // Dynamic filters removed
          };

          console.log('Private research parameters object:', researchParameters);
          
          console.log('[usePrivateResearchGeneration] About to save research with:', {
            workbook_id: params.workbook_id || null,
            user_id: user.id,
            timestamp: new Date().toISOString()
          });

          // Save the research to workbook_research table
          const { data: savedResearch, error: saveError } = await supabase
            .from('workbook_research')
            .insert({
              user_id: user.id,
              workbook_id: params.workbook_id || null, // Direct assignment to project
              page_id: params.page_id || null,
              title,
              content: researchData.content,
              research_type: effectiveResearchType,
              original_prompt: params.researchPrompt,
              metadata: researchData.metadata || {},
              sources: researchData.sources || [],
              related_questions: researchData.related_questions || [],
              usage: researchData.usage || {},
              research_parameters: researchParameters,
              system_prompt: researchData.system_prompt || null,
              generated_research_count: 0, // Default for new research
            })
            .select()
            .single();

          if (saveError) {
            console.error('Error saving private research:', saveError);
            throw new Error(`Failed to save private research: ${saveError.message}`);
          }

          console.log('[usePrivateResearchGeneration] Private research saved successfully:', {
            id: savedResearch.id,
            workbook_id: savedResearch.workbook_id,
            title: savedResearch.title,
            timestamp: new Date().toISOString()
          });
          
          return savedResearch;
        },
        {
          immediate: true, // Research generation needs immediate feedback
          errorPrefix: 'Private research generation failed',
          debug: true // Enable debug logging for research generation
        }
      );
    },
    onSuccess: (data) => {
      console.log('Private research generation successful, invalidating queries');
      toast.success('Private research generated successfully!');
      
      // Trigger quest completion for Step 2 (Generate Research)
      QuestTriggers.researchGenerated();
      
      // CRITICAL FIX: Use refetchType: 'all' instead of 'active' to force immediate refetch
      // This ensures the empty state page immediately shows the new content
      queryClient.invalidateQueries({ 
        queryKey: ['private-research-generator'],
        exact: false,
        refetchType: 'all'  // CHANGED: Force refetch of all cached variations
      });
      
      // Invalidate count queries  
      queryClient.invalidateQueries({ 
        queryKey: ['private-research-generator-count'],
        exact: false,
        refetchType: 'all'  // CHANGED: Force immediate refetch
      });
      
      // CRITICAL: Invalidate the total user research query that controls hasData/empty state
      queryClient.invalidateQueries({
        queryKey: ['private-research-total'],
        exact: false,
        refetchType: 'all'
      });
      
      // Also invalidate universal storage queries for complete cache refresh
      queryClient.invalidateQueries({
        queryKey: ['universal-storage-items'],
        exact: false,
        refetchType: 'all'
      });
      
      queryClient.invalidateQueries({
        queryKey: ['universal-library-data'],
        exact: false,
        refetchType: 'all'
      });
      
      // Invalidate library counts (for sidebar counters)
      queryClient.invalidateQueries({
        queryKey: ['universal-library-counts'],
        exact: false,
        refetchType: 'all'
      });
      
      // CRITICAL: Invalidate workbook research queries to update project resources tab
      queryClient.invalidateQueries({
        queryKey: ['workbook-research'],
        exact: false,
        refetchType: 'all'
      });
      
      console.log('Private research query invalidation completed');
    },
    onError: (error) => {
      console.error('Private research generation error:', error);
      toast.error(`Failed to generate private research: ${error.message}`);
    },
  });
}; 